import { ErrorBoundary, Button, HStack, VStack } from '@genial/components';
import { Typography, BoxRadius } from '@genial/design-tokens';
import { Box, FlatList, ScrollView, View } from 'native-base';
import React from 'react';

import { OffersInfoDataProps, ProductsDataProps } from '../../utils/types';

import { OfferSummary } from './components/OfferSummary';
import { OfferSummaryHeader } from './components/OfferSummaryHeader';
import { ProductAccordion } from './components/ProductAccordion';
import { ProductTable } from './components/ProductTable';
import { SkeletonItem } from './components/SkeletonItem';

interface CustomerOffersProps {
  retryBatchTicketInvestLink: () => void;
  handleConfirmOrder: () => void;
  handleRefuseOrder: () => void;
  handleToggleOrderStatus: (id: string) => void;
  listBatchTicketLink: ProductsDataProps[];
  errorBatchTicketLink: Error | undefined;
  loadingBatchTicketLink: boolean;
  offersInfoData: OffersInfoDataProps;
}

export const ContainerDesktop: React.FC<CustomerOffersProps> = ({
  retryBatchTicketInvestLink,
  handleConfirmOrder,
  handleRefuseOrder,
  handleToggleOrderStatus,
  listBatchTicketLink,
  errorBatchTicketLink,
  loadingBatchTicketLink,
  offersInfoData,
}) => {
  if (loadingBatchTicketLink) {
    return <SkeletonItem />;
  }

  if (errorBatchTicketLink) {
    return <ErrorBoundary onRefresh={retryBatchTicketInvestLink} {...errorBatchTicketLink} />;
  }

  if (!loadingBatchTicketLink && !errorBatchTicketLink && listBatchTicketLink?.length === 0) {
    return (
      <Box py={{ sm: 'inline.md' }}>
        <ErrorBoundary
          onRefresh={retryBatchTicketInvestLink}
          errorIconOrIllustration="NotEmptyIllustration"
          errorTitle="Nenhuma oferta encontrada."
          errorMessage="No momento não há nenhuma oferta disponível."
        />
      </Box>
    );
  }

  const groupedProductData = listBatchTicketLink.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {} as Record<string, ProductsDataProps[]>);

  const typeOrder = Object.keys(groupedProductData);

  return (
    <View minW="container" maxWidth={900} pt="inline.xs" px="inline.xxxs">
      <HStack space="inline.xxxs" flex={1} flexDirection={'row'}>
        <ScrollView flex={1} flexGrow={1} showsVerticalScrollIndicator={false}>
          <VStack space="inline.xxxs">
            {typeOrder?.map((type, index) => (
              <VStack key={type} bg={'neutral.white'} borderRadius={BoxRadius.sm} p="inline.xxxs">
                <ProductAccordion
                  title={type}
                  initiallyOpen={index === 0}
                  content={groupedProductData[type].map((product) => (
                    <ProductTable
                      key={product.id}
                      productData={product}
                      handleToggleOrderStatus={handleToggleOrderStatus}
                    />
                  ))}
                />
              </VStack>
            ))}
          </VStack>
        </ScrollView>
        <VStack space="inline.xxxs" flexShrink={0}>
          <VStack minWidth={360} borderRadius="inset.nano" space="stack.xxs" pt="stack.xxs">
            <OfferSummaryHeader
              totalValue={offersInfoData.totalCustomerOffersAmount}
              orderHash={offersInfoData.basketAdvisorOrderHash}
            />
            <FlatList
              showsVerticalScrollIndicator={false}
              data={offersInfoData.headerOrderData}
              ItemSeparatorComponent={() => (
                <Box h="0.5px" my="stack.nano" w="full" bg="neutral.lightest" />
              )}
              keyExtractor={(item) => item.label}
              renderItem={({ item }) => <OfferSummary item={item} />}
            />
          </VStack>

          <VStack space="stack.xxxs">
            <Button
              onPress={handleConfirmOrder}
              p="inline.nano"
              w={{ base: 'full', sm: 'container' }}
              minH="inline.sm"
              _text={{
                fontFamily: Typography['fontfamily.base.semibold'],
              }}>
              Confirmar
            </Button>
            <Button
              variant="secondary"
              onPress={handleRefuseOrder}
              p="inline.nano"
              w={{ base: 'full', sm: 'container' }}
              minH="inline.sm"
              _text={{
                fontFamily: Typography['fontfamily.base.semibold'],
              }}>
              Recusar
            </Button>
          </VStack>
        </VStack>
      </HStack>
    </View>
  );
};
